// src/components/molecules/MenuItem.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ref, onValue } from 'firebase/database';
import { database } from 'config/firebase';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Plus, Minus, Trash } from 'lucide-react';
import { useCart } from 'context/CartContext';
import { useModal } from 'context/ModalContext';
import useViewport from 'hooks/useViewport';
import { useSiteInfo } from 'hooks/useSiteInfo';

// Import components
import RemovalModal from 'components/modals/RemoveModal';
import ItemDetailModal from 'components/modals/ItemDetailModal';

// Styled components
const StyledMenuItem = styled.div`
  background-color: ${({ theme }) => theme.colors.offWhite};
  background-image:
    linear-gradient(rgba(241, 239, 232, 0.62), rgba(241, 239, 232, 0.62)),
    ${({ flipped }) =>
      flipped
        ? "url('https://storage.googleapis.com/birreria-el-semaforo/images/left-background/l.webp')"
        : "url('https://storage.googleapis.com/birreria-el-semaforo/images/right-background/l.webp')"};
  background-size: cover;
  background-position: bottom center;

  ${({ theme }) => theme.mediaQueries.md} {
    background-image:
      linear-gradient(rgba(241, 239, 232, 0.62), rgba(241, 239, 232, 0.62)),
      url('https://storage.googleapis.com/birreria-el-semaforo/images/left-background/l.webp');
  }
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 0;
  height: 100%;
  min-height: 130px;
  display: grid;
  align-items: stretch;
  grid-template-columns: ${({ hasImage, flipped }) => {
    if (!hasImage) return '1fr';
    return flipped ? '58% 42%' : '42% 58%';
  }};
  gap: 0;
  max-width: 100%;
  box-sizing: border-box;
  transition: ${({ shoppingEnabled }) =>
    shoppingEnabled ? 'all 0.3s ease' : 'none'};
  border: 1px solid rgba(160, 130, 90, 0.25);
  position: relative;
  overflow: hidden;

  ${({ theme }) => theme.mediaQueries.sm} {
    &:hover {
      box-shadow: ${({ shoppingEnabled }) =>
        shoppingEnabled
          ? '0 4px 16px rgba(0, 0, 0, 0.12)'
          : '0 2px 8px rgba(0, 0, 0, 0.08)'};
      transform: ${({ shoppingEnabled }) =>
        shoppingEnabled ? 'translateY(-2px)' : 'none'};
    }
  }

  ${({ theme }) => theme.mediaQueries.md} {
    padding: 2rem;
    min-height: 220px;
    gap: 1.5rem;
    align-items: initial;
    grid-template-columns: ${({ hasImage }) =>
      hasImage ? '1fr 180px' : '1fr'};
  }
`;

const MenuItemContent = styled.div`
  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 0.5rem;
  min-width: 0;
  height: 100%;
  padding: 0.875rem;

  ${({ theme }) => theme.mediaQueries.md} {
    gap: 0.75rem;
    padding: 0;
  }
`;

const MenuItemImageContainer = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 0;
  overflow: hidden;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.offWhite} 0%,
    ${({ theme }) => theme.colors.lightGrey} 100%
  );
  position: relative;
  cursor: pointer;
  transition: transform 0.2s ease;
  order: ${({ flipped }) => (flipped ? 0 : -1)};

  &:hover {
    transform: scale(1.02);
  }

  &:active {
    transform: scale(0.98);
  }

  ${({ theme }) => theme.mediaQueries.md} {
    aspect-ratio: 1;
    height: auto;
    border-radius: 8px;
    order: 0;
  }
`;

const MenuItemImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${({ isLoaded }) => (isLoaded ? 1 : 0)};
  transition: opacity 0.3s ease-in-out;
`;

const ImagePlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.darkGrey};
  font-size: 0.875rem;
  opacity: ${({ show }) => (show ? 1 : 0)};
  transition: opacity 0.3s ease-in-out;
`;

const MenuItemHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: auto;
`;

const MenuItemName = styled.h2`
  ${({ theme }) => theme.mixins.textP1};
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 700;
  margin: 0;
  line-height: 1.3;
  letter-spacing: -0.01em;
`;

const MenuItemDescription = styled.p`
  ${({ theme }) => theme.mixins.textP3};
  color: ${({ theme }) => theme.colors.darkGrey};
  flex-grow: 1;
  max-width: 100%;
  margin: 0;
  line-height: 1.5;
  padding-bottom: 0.5rem;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`;

const ReadMoreButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 1.2rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.tertiaryDark};
  cursor: pointer;
  display: inline;
  margin-left: 4px;
  -webkit-tap-highlight-color: rgba(25, 0, 189, 0.1);

  &:hover {
    text-decoration: none;
  }
`;

const MenuItemFooter = styled.div`
  display: flex;
  align-items: center;
  margin-top: auto;
  width: 100%;
  max-width: 100%;
  padding-top: 0.5rem;
  justify-content: flex-start;
`;

const MenuItemFooterTop = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 100%;
  gap: 0.5rem;
`;

const StyledButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ isActive, theme }) =>
    isActive ? theme.colors.secondaryDark : 'rgba(255, 255, 255, 0.72)'};
  color: ${({ isActive, theme }) =>
    isActive ? theme.colors.white : theme.colors.secondaryDark};
  border: 1.5px solid ${({ theme }) => theme.colors.secondaryDark};
  border-radius: 14px;
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 0.03em;
  transition: ${({ shoppingEnabled }) =>
    shoppingEnabled ? 'all 0.25s ease' : 'none'};
  cursor: pointer;
  position: relative;
  overflow: visible;
  height: 40px;
  padding: 0 1rem;
  flex: 1;
  min-width: 0;
  max-width: 140px;
  gap: 0.5rem;
  box-shadow: ${({ isActive, theme }) =>
    isActive ? `0 4px 14px ${theme.colors.secondaryDark}40` : 'none'};

  ${({ theme }) => theme.mediaQueries.md} {
    height: 40px;
    padding: 0 1.25rem;
    flex: initial;
    min-width: 140px;
  }

  ${({ theme }) => theme.mediaQueries.sm} {
    &:hover {
      background-color: ${({ shoppingEnabled, theme }) =>
        shoppingEnabled
          ? theme.colors.secondaryDark
          : 'rgba(255, 255, 255, 0.72)'};
      color: ${({ shoppingEnabled, theme }) =>
        shoppingEnabled ? theme.colors.white : theme.colors.secondaryDark};
      transform: ${({ shoppingEnabled }) =>
        shoppingEnabled ? 'translateY(-1px)' : 'none'};
      box-shadow: ${({ shoppingEnabled, theme }) =>
        shoppingEnabled
          ? `0 4px 14px ${theme.colors.secondaryDark}40`
          : 'none'};
    }

    &:active {
      transform: ${({ shoppingEnabled }) =>
        shoppingEnabled ? 'translateY(0)' : 'none'};
    }
  }
`;

const PriceSection = styled.span`
  ${({ theme }) => theme.mixins.textP3};
  font-weight: 400;
  text-align: center;
  font-size: 1.5rem;
  letter-spacing: 0.05em;
`;

const IconSection = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  border-left: 1.5px solid
    ${({ isActive, theme }) =>
      isActive ? 'rgba(255, 255, 255, 0.4)' : theme.colors.secondaryDark};
  padding-left: 0.5rem;
  margin-left: 0.5rem;
`;

const CartQuantityIndicator = styled.div`
  position: absolute;
  /* The button sits flush with the card's clipped right edge and the icon
     wrapper extends ~7px past it; a negative offset sliced the badge in half
     on phones. 8px keeps the whole badge inside the card (#245). */
  top: -8px;
  right: 8px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 10;
  border: 2px solid white;
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: flex-start;
`;

const QuantityControlWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
`;

const QuantityControlButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1.5px solid ${({ theme }) => theme.colors.secondaryDark};
  border-radius: 14px;
  background-color: transparent;
  color: ${({ theme }) => theme.colors.secondaryDark};
  cursor: pointer;
  letter-spacing: 0.03em;
  transition: ${({ shoppingEnabled }) =>
    shoppingEnabled ? 'all 0.25s ease' : 'none'};
  flex-shrink: 0;

  ${({ theme }) => theme.mediaQueries.md} {
    width: 40px;
    height: 40px;
  }

  ${({ theme }) => theme.mediaQueries.sm} {
    &:hover {
      background-color: ${({ shoppingEnabled, theme }) =>
        shoppingEnabled ? theme.colors.secondaryDark : 'transparent'};
      color: ${({ shoppingEnabled, theme }) =>
        shoppingEnabled ? theme.colors.white : theme.colors.secondaryDark};
      transform: ${({ shoppingEnabled }) =>
        shoppingEnabled ? 'translateY(-1px)' : 'none'};
    }

    &:active {
      transform: ${({ shoppingEnabled }) =>
        shoppingEnabled ? 'translateY(0)' : 'none'};
    }
  }
`;

const ItemRemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin-right: 0.5rem;
  border: 1.5px solid ${({ theme }) => theme.colors.primary};
  border-radius: 14px;
  background: ${({ theme }) => `${theme.colors.primary}14`};
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  letter-spacing: 0.03em;
  transition: ${({ shoppingEnabled }) =>
    shoppingEnabled ? 'all 0.25s ease' : 'none'};
  flex-shrink: 0;

  ${({ theme }) => theme.mediaQueries.md} {
    width: 40px;
    height: 40px;
  }

  ${({ theme }) => theme.mediaQueries.sm} {
    &:hover {
      background-color: ${({ shoppingEnabled, theme }) =>
        shoppingEnabled ? theme.colors.primary : `${theme.colors.primary}14`};
      color: ${({ shoppingEnabled, theme }) =>
        shoppingEnabled ? theme.colors.white : theme.colors.primary};
      transform: ${({ shoppingEnabled }) =>
        shoppingEnabled ? 'translateY(-1px)' : 'none'};
    }

    &:active {
      transform: ${({ shoppingEnabled }) =>
        shoppingEnabled ? 'translateY(0)' : 'none'};
    }
  }
`;

/**
 * MenuItem component displays a menu item with customization options
 * @param {Object} item - The menu item to display
 * @returns {React.Component} - The MenuItem component
 */
const MenuItem = ({ item, categoryId, index = 0 }) => {
  // Adding to the cart stays possible while closed: the customer books a
  // time at checkout (#485). The styled props below keep the old name.
  const { orderingEnabled: shoppingEnabled } = useSiteInfo();
  const flipped = index % 2 !== 0;
  const [categoryData, setCategoryData] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  // Generate image URLs with WebP support and fallbacks
  const getImageUrls = useCallback(() => {
    // If we have a real image from Clover, use it
    if (item.imageUrl) {
      // Check if this is a Clover CDN URL
      const isCloverCDN = item.imageUrl.includes('cloverstatic.com');

      // Clover CDN doesn't serve WebP, so skip WebP conversion for Clover images
      if (isCloverCDN) {
        return {
          fallback: item.imageUrl,
          alt: item.name,
        };
      }

      // For non-Clover images, provide WebP with fallback
      return {
        webp: item.imageUrl.replace(/\.(jpg|jpeg|png)$/i, '.webp'),
        fallback: item.imageUrl,
        alt: item.name,
      };
    }

    return null;
  }, [item.imageUrl, item.name]);

  const imageData = getImageUrls();

  useEffect(() => {
    if (categoryId) {
      // Note: This reads from language-specific menu via CloverContext
      // Admin updates go to /menus/en/latest
      const categoryRef = ref(
        database,
        `/menus/en/latest/categories/${categoryId}`,
      );
      const unsubscribeCategory = onValue(categoryRef, (snapshot) => {
        if (snapshot.exists()) {
          setCategoryData(snapshot.val());
        }
      });
      return () => unsubscribeCategory();
    }
  }, [categoryId]);

  const {
    addToCart,
    removeCartItem,
    updateCartItemQuantity,
    findCartItemsByBaseId,
  } = useCart();
  const { openModal } = useModal();

  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [isRemoveAllModalOpen, setIsRemoveAllModalOpen] = useState(false);
  const [isRemoveDrawerOpen, setIsRemoveDrawerOpen] = useState(false);
  const [isRemoveAllDrawerOpen, setIsRemoveAllDrawerOpen] = useState(false);
  const [variantsInCart, setVariantsInCart] = useState([]);

  const { isMobile } = useViewport();

  // Check if description needs truncation (simple character limit)
  const MAX_DESCRIPTION_LENGTH = 150;
  const shouldTruncate =
    item.description && item.description.length > MAX_DESCRIPTION_LENGTH;
  const displayText =
    shouldTruncate && !isDescriptionExpanded
      ? item.description.slice(0, MAX_DESCRIPTION_LENGTH).trim()
      : item.description;

  const handleExpandClick = (e) => {
    e.stopPropagation();
    setIsDescriptionExpanded(!isDescriptionExpanded);
  };

  const handleDescriptionClick = (e) => {
    if (shouldTruncate) {
      handleExpandClick(e);
    }
  };

  const hasModifierGroups =
    item.modifierGroups && item.modifierGroups.length > 0;
  const hasAddOns = item.addOns && item.addOns.length > 0;
  const needsCustomization = hasModifierGroups || hasAddOns;

  const getAllCartItemsForBaseItem = useCallback(() => {
    return findCartItemsByBaseId(item.id || item.name);
  }, [findCartItemsByBaseId, item.id, item.name]);

  const quantity = useMemo(() => {
    const cartItems = getAllCartItemsForBaseItem();
    return cartItems.reduce((sum, cartItem) => sum + cartItem.quantity, 0);
  }, [getAllCartItemsForBaseItem]);

  const getItemId = useCallback(() => {
    return item.id || item.name;
  }, [item.id, item.name]);

  const getCurrentPrice = useCallback(() => {
    if (typeof item.price === 'number') {
      return `$${(item.price / 100).toFixed(2)}`;
    }
    return item.price;
  }, [item.price]);

  const toggleRemoveModal = () => {
    const cartItemsForThisItem = getAllCartItemsForBaseItem();
    setVariantsInCart(
      cartItemsForThisItem.map((cartItem) => {
        const modifierNames = cartItem.modifiers
          ?.map((mod) => mod.name)
          .join(', ');
        return {
          id: cartItem.cartItemId,
          name: modifierNames || 'Standard',
          quantity: cartItem.quantity,
        };
      }),
    );

    if (!isMobile) {
      setIsRemoveModalOpen(true);
    } else {
      setIsRemoveDrawerOpen(true);
    }
  };

  const toggleRemoveAllModal = () => {
    const cartItemsForThisItem = getAllCartItemsForBaseItem();
    setVariantsInCart(
      cartItemsForThisItem.map((cartItem) => {
        const modifierNames = cartItem.modifiers
          ?.map((mod) => mod.name)
          .join(', ');
        return {
          id: cartItem.cartItemId,
          name: modifierNames || 'Standard',
          quantity: cartItem.quantity,
        };
      }),
    );

    if (!isMobile) {
      setIsRemoveAllModalOpen(true);
    } else {
      setIsRemoveAllDrawerOpen(true);
    }
  };

  const closeRemoveModal = () => {
    setIsRemoveModalOpen(false);
    setIsRemoveDrawerOpen(false);
  };

  const closeRemoveAllModal = () => {
    setIsRemoveAllModalOpen(false);
    setIsRemoveAllDrawerOpen(false);
  };

  const removeVariant = (cartItemId) => {
    removeCartItem(cartItemId);
    closeRemoveModal();
  };

  const removeAllVariants = () => {
    variantsInCart.forEach((variant) => {
      removeCartItem(variant.id);
    });
    closeRemoveAllModal();
  };

  const toggleAddOnsUI = () => {
    if (needsCustomization) {
      openModal(item);
    } else if (!quantity) {
      addToCart({ ...item, id: getItemId() });
    }
  };

  const handleIncrement = (e) => {
    e.stopPropagation();

    if (hasModifierGroups) {
      openModal(item);
    } else {
      addToCart({
        ...item,
        id: getItemId(),
        displayPrice: getCurrentPrice(),
        originalItem: { ...item },
      });
    }
  };

  const handleDecrement = (e) => {
    e.stopPropagation();

    if (hasModifierGroups) {
      const cartItemsForThisItem = getAllCartItemsForBaseItem();

      if (cartItemsForThisItem.length === 1) {
        const cartItem = cartItemsForThisItem[0];
        if (cartItem.quantity <= 1) {
          removeCartItem(cartItem.cartItemId);
        } else {
          updateCartItemQuantity(cartItem.cartItemId, cartItem.quantity - 1);
        }
      } else if (cartItemsForThisItem.length > 1) {
        toggleRemoveModal();
      }
    } else {
      const cartItemsForThisItem = getAllCartItemsForBaseItem();
      if (cartItemsForThisItem.length > 0) {
        const cartItem = cartItemsForThisItem[0];
        if (cartItem.quantity <= 1) {
          removeCartItem(cartItem.cartItemId);
        } else {
          updateCartItemQuantity(cartItem.cartItemId, cartItem.quantity - 1);
        }
      }
    }
  };

  const handleRemoveItems = (e) => {
    e && e.stopPropagation();

    if (hasModifierGroups) {
      const cartItemsForThisItem = getAllCartItemsForBaseItem();

      if (cartItemsForThisItem.length === 1) {
        removeCartItem(cartItemsForThisItem[0].cartItemId);
      } else if (cartItemsForThisItem.length > 1) {
        toggleRemoveAllModal();
      }
    } else {
      const cartItemsForThisItem = getAllCartItemsForBaseItem();
      cartItemsForThisItem.forEach((cartItem) => {
        removeCartItem(cartItem.cartItemId);
      });
    }
  };

  const renderCartButton = () => {
    return (
      <ButtonContainer>
        {quantity > 0 && (
          <ItemRemoveButton
            shoppingEnabled={shoppingEnabled}
            onClick={handleRemoveItems}
            aria-label={`Remove all ${item.name} from cart`}
          >
            <Trash size={20} />
          </ItemRemoveButton>
        )}

        <QuantityControlWrapper>
          {quantity > 0 && (
            <QuantityControlButton
              shoppingEnabled={shoppingEnabled}
              onClick={handleDecrement}
              aria-label={`Decrease quantity of ${item.name}`}
            >
              <Minus size={20} />
            </QuantityControlButton>
          )}
          <StyledButton
            shoppingEnabled={shoppingEnabled}
            onClick={
              quantity > 0
                ? hasModifierGroups
                  ? toggleAddOnsUI
                  : handleIncrement
                : needsCustomization
                  ? toggleAddOnsUI
                  : () => {
                      addToCart({
                        ...item,
                        id: getItemId(),
                        displayPrice: getCurrentPrice(),
                        originalItem: { ...item },
                      });
                    }
            }
            isActive={quantity > 0}
            aria-label={`${quantity > 0 ? 'Add more' : needsCustomization ? 'Customize' : 'Add'} ${item.name} to cart`}
          >
            <PriceSection>{getCurrentPrice()}</PriceSection>
            <IconSection isActive={quantity > 0}>
              <Plus size={20} />
              {quantity > 0 && (
                <CartQuantityIndicator>{quantity}</CartQuantityIndicator>
              )}
            </IconSection>
          </StyledButton>
        </QuantityControlWrapper>
      </ButtonContainer>
    );
  };

  const canSellOnline = useMemo(() => {
    if (!shoppingEnabled) return false;
    if (categoryData && categoryData.isSoldOnline === false) return false;
    if (item.isSoldOnline === false) return false;
    return true;
  }, [shoppingEnabled, categoryData, item.isSoldOnline]);

  return (
    <StyledMenuItem
      shoppingEnabled={shoppingEnabled}
      hasImage={!!imageData}
      flipped={flipped}
    >
      <MenuItemContent>
        <MenuItemHeader>
          <MenuItemName>{item.name}</MenuItemName>
          <MenuItemDescription
            $clickable={shouldTruncate}
            onClick={handleDescriptionClick}
          >
            {displayText}
            {shouldTruncate && (
              <ReadMoreButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleExpandClick(e);
                }}
              >
                {isDescriptionExpanded ? 'Read less' : 'Read more'}
              </ReadMoreButton>
            )}
          </MenuItemDescription>
        </MenuItemHeader>

        <MenuItemFooter>
          {canSellOnline ? (
            <MenuItemFooterTop>{renderCartButton()}</MenuItemFooterTop>
          ) : (
            <StyledButton shoppingEnabled={shoppingEnabled}>
              <PriceSection>{getCurrentPrice()}</PriceSection>
            </StyledButton>
          )}
        </MenuItemFooter>
      </MenuItemContent>

      {imageData ? (
        <MenuItemImageContainer
          flipped={flipped}
          onClick={() => setIsDetailModalOpen(true)}
        >
          <picture>
            {imageData.webp && (
              <source
                srcSet={imageData.webp}
                type="image/webp"
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
              />
            )}
            <MenuItemImage
              src={imageData.fallback || imageData.jpg}
              alt={imageData.alt || item.name}
              loading="lazy"
              isLoaded={imageLoaded}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
            />
          </picture>
          {!imageLoaded && !imageError && (
            <ImagePlaceholder show={true}>
              <span className="sr-only">Loading {item.name} image</span>
            </ImagePlaceholder>
          )}
        </MenuItemImageContainer>
      ) : null}

      <RemovalModal
        isRemoveDrawerOpen={isRemoveDrawerOpen}
        isRemoveModalOpen={isRemoveModalOpen}
        isRemoveAllDrawerOpen={isRemoveAllDrawerOpen}
        isRemoveAllModalOpen={isRemoveAllModalOpen}
        closeRemoveModal={closeRemoveModal}
        closeRemoveAllModal={closeRemoveAllModal}
        removeVariant={removeVariant}
        removeAllVariants={removeAllVariants}
        variantsInCart={variantsInCart}
        item={item}
      />

      <ItemDetailModal
        isOpen={isDetailModalOpen}
        item={item}
        onClose={() => setIsDetailModalOpen(false)}
        categoryId={categoryId}
      />
    </StyledMenuItem>
  );
};

MenuItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    price: PropTypes.number,
    prepTime: PropTypes.string,
    isSoldOnline: PropTypes.bool,
    image: PropTypes.string,
    modifierGroups: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        required: PropTypes.bool,
        minRequired: PropTypes.number,
        maxAllowed: PropTypes.number,
        modifiers: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            price: PropTypes.number,
            available: PropTypes.bool,
          }),
        ),
      }),
    ),
    addOns: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        price: PropTypes.number,
      }),
    ),
  }).isRequired,
  categoryId: PropTypes.string,
  index: PropTypes.number,
};

export default MenuItem;
